import React from 'react';
import { dpsTheme } from '../../../styles/dpsTheme';
import { ProgressStats } from '../../../services/progressTrackingService';
import { useDeviceSize } from '../../../hooks/useMediaQuery';

interface GlobalStatsCardsProps {
  stats: ProgressStats | null;
}

export const GlobalStatsCards: React.FC<GlobalStatsCardsProps> = ({ stats }) => {
  const { isMobile, isTablet } = useDeviceSize();

  if (!stats) {
    return null;
  }

  const statsCards = [
    {
      value: stats.completedActions,
      label: 'Actions réalisées',
      color: dpsTheme.colors.accent.green.primary,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )
    },
    {
      value: stats.inProgressActions,
      label: 'Actions en cours',
      color: '#f59e0b',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      )
    },
    {
      value: stats.plannedActions,
      label: 'Actions planifiées',
      color: dpsTheme.colors.accent.blue.secondary,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      )
    },
    {
      value: `${stats.totalInvestment.toLocaleString('fr-FR')} €`,
      label: 'Total investi',
      color: dpsTheme.colors.text.primary,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      )
    }
  ];

  return (
    <section style={{
      background: dpsTheme.colors.background.card,
      backdropFilter: 'blur(20px)',
      border: `1px solid ${dpsTheme.colors.border.primary}`,
      borderRadius: dpsTheme.borderRadius.lg,
      padding: isMobile ? dpsTheme.spacing.lg : dpsTheme.spacing['2xl'],
      marginBottom: dpsTheme.spacing['2xl']
    }}>
      <h2 style={{
        fontSize: dpsTheme.typography.fontSize.xl,
        fontWeight: dpsTheme.typography.fontWeight.semibold,
        color: dpsTheme.colors.text.primary,
        marginBottom: dpsTheme.spacing.xl,
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
        Statistiques globales
      </h2>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: dpsTheme.spacing.lg
      }}>
        {statsCards.map((card, index) => (
          <div
            key={index}
            style={{
              background: dpsTheme.colors.background.darker,
              border: `1px solid ${dpsTheme.colors.border.primary}`,
              borderRadius: dpsTheme.borderRadius.md,
              padding: dpsTheme.spacing.xl,
              textAlign: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.borderColor = card.color;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = dpsTheme.colors.border.primary;
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 16px',
              background: `${card.color}20`,
              border: `1px solid ${card.color}40`,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: card.color
            }}>
              {card.icon}
            </div>
            <div style={{
              fontSize: '36px',
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: card.color,
              marginBottom: '8px',
              lineHeight: 1
            }}>
              {card.value}
            </div>
            <div style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.text.muted
            }}>
              {card.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
